import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { bankAccounts } from '../lib/cashBookGrid.js';
import { parseAbsaStatement } from '../lib/absaStatement.js';
import {
  ackKeyForChoice, approvableChoices, chosenJournalLineId,
  bandPresentation, outcomeMeta, filterQueue, centsToAmount, amountColumns,
  formatConfidence, proposedCandidate, candidateUnavailable,
} from '../lib/bankRecScreen.js';

/**
 * Bank reconciliation — the screen the milestone is judged by.
 *
 * Task 8 of docs/bank-reconciliation-milestone-plan.md, modelled on
 * SlipReview.jsx because it is the same shape of job: a queue of things needing
 * a decision, the proposal beside the evidence, accept or reject. The owner's
 * sentence is the whole design — **"it matches but we approve it"** — and every
 * choice below is either an instance of it or a defence of it.
 *
 * ---------------------------------------------------------------------------
 * THE THREE THINGS THIS SCREEN MUST NOT DO
 * ---------------------------------------------------------------------------
 *
 * 1. IT NEVER SCORES A MATCH. Decision 9 of the plan. `slips.js:567-581` and
 *    `SlipReview.jsx:236-245` record an earlier screen re-deriving a proposal
 *    client-side and drifting "from the real matching in ways that mattered",
 *    in three specific ways. So `confidence`, `band` and `basis` all arrive on
 *    the wire from `POST /api/bank-statements/:id/matches`, and this file only
 *    renders them. There is no threshold, no weight and no scoring function
 *    anywhere in `web/src/`.
 *
 * 2. IT NEVER RE-WORDS `basis`. That sentence is the point of the screen:
 *    approving a match you cannot check is not approval, it is a signature.
 *    The server writes it once, in `proposeFromHistory`'s voice — a fact, no
 *    verdict, never the score (spec §2) — naming dates and words the reader can
 *    see on their own statement. It is rendered verbatim, in the queue AND in
 *    the detail panel.
 *
 * 3. NOTHING AUTO-APPROVES. `autoApply` is false unconditionally on the wire
 *    and there is deliberately no rule that loosens it (`allocationRules.js:9-11`
 *    is this gate's ancestor). A `confident` band pre-selects a radio button
 *    and buys nothing else; the tick is still required, and the tick is keyed.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE ACKNOWLEDGEMENT IS KEYED, AND THIS IS THE MOST DANGEROUS THING HERE
 * ---------------------------------------------------------------------------
 *
 * `SlipReview.jsx:266-281` records a real bug: a boolean acknowledgement reset
 * by a `useEffect` did not re-fire when two consecutive slips proposed the same
 * pair, because React bails out of an effect whose dependencies are
 * referentially equal to last render's — and "a tick from the PREVIOUS slip
 * silently authorised the next post."
 *
 * THIS SCREEN HAS THAT HAZARD MULTIPLIED, THREE TIMES OVER:
 *
 *  - The rows really are identical. The plan measured it on the owner's own
 *    export: 53 rows share a `(date, amount, description)` triple, eleven
 *    identical `PROOF OF PMT EMAIL` on 30 July alone. Spec §6.1 makes it the
 *    flagship fixture — S1 and S2 against J1 and J2, every pair at 0.800.
 *  - The queue is long, so ticking runs down a column and consecutive rows are
 *    the normal case rather than the unlucky one.
 *  - There is a BULK button. Where the slip screen risked one wrong post, this
 *    one sends every ticked row at once.
 *
 * So there is no boolean anywhere on this screen and no effect resets an
 * acknowledgement. `acked` is a SET OF KEYS — `(statementLineId, journalLineId,
 * confidence)`, see `lib/bankRecScreen.js` — and `approvableChoices` derives
 * what the button sends FROM THAT SET. Three consequences, all structural:
 *
 *  - A tick on row 212 cannot reach row 213, however identical they look,
 *    because the key holds the line's identity and not its content.
 *  - Changing the picked candidate changes the key, so the previous tick lapses
 *    with no reset to run.
 *  - A matcher re-run that swaps or re-scores a proposal produces a different
 *    key, so a tick given for evidence nobody is being shown any more is
 *    silently left out rather than silently sent.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY NOT HERE
 * ---------------------------------------------------------------------------
 *
 * THE "CAPTURE IT" BUTTON. Spec §5 designs it and `routes/bankReconciliation.js`
 * records that the route is not written yet. Rather than invent a client for an
 * endpoint that does not exist, a residual line says what is actually true and
 * names the whole journey — capture in the cash book, post the batch, run the
 * matcher again, approve — which is the sentence spec §5 says to keep, because
 * the temptation at every one of those four steps is to collapse it into "we
 * know what this is, just mark it reconciled".
 *
 * THE RECONCILIATION STATEMENT. Reconciled balance against the statement's
 * closing balance, with the unmatched items on BOTH sides making up the
 * difference, is task 9 and is a report. This screen shows the statement's own
 * two balances because the file proved them; it does not compute a difference
 * it has no ledger figures for.
 */

/** Small monospace fact, the way every figure on this screen is set. */
const Fact = ({ label, children }) => (
  <div className="field">
    <span>{label}</span>
    <span className="figure">{children}</span>
  </div>
);

/**
 * The bank's own line, and the ledger's answer to it, side by side.
 *
 * The bank's narrative is set in the monospace face and NOT re-wrapped or
 * tidied: the description is fixed-width padded as the bank printed it, and a
 * bookkeeper checking this against the PDF in their other hand is comparing
 * character for character.
 */
function LineDetail({
  line, picked, acked, canApprove, busy, onPick, onToggleAck, onApprove, onReject,
}) {
  const meta = outcomeMeta(line.outcome);
  const chosen = chosenJournalLineId(line, picked);
  const ackKey = ackKeyForChoice(line, chosen);
  // Does the tick cover exactly THIS pairing on THIS line at THIS score? No
  // reset effect exists, and none is needed — the acknowledgement IS the key,
  // so it can only ever match the one combination it was given for.
  const acknowledged = Boolean(ackKey) && acked.has(ackKey);
  const proposed = proposedCandidate(line);
  const band = line.proposal ? bandPresentation(line.proposal.band) : null;
  const { moneyIn, moneyOut } = amountColumns(line.amountCents);

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'minmax(240px, 380px) 1fr', gap: 24,
      marginTop: 26, paddingTop: 22, borderTop: '1px solid var(--rule)',
    }}>
      <div>
        <h2 className="eyebrow" style={{ margin: '0 0 10px' }}>What the bank says</h2>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <Fact label="Date">{line.transactionDate}</Fact>
          <Fact label="Line in your file">{line.sourceRowNo}</Fact>
          <Fact label="Out">{moneyOut ? money(moneyOut) : '—'}</Fact>
          <Fact label="In">{moneyIn ? money(moneyIn) : '—'}</Fact>
        </div>
        <div className="field" style={{ marginTop: 4 }}>
          <span>Narrative, as printed</span>
          <span className="figure" style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {line.description}
          </span>
        </div>
      </div>

      <div>
        <h2 className="eyebrow" style={{ margin: '0 0 10px' }}>
          {meta.label}
          {band && (
            <>
              {' · '}<span style={{ color: 'var(--debit)' }}>{band.label}</span>
              {/* The server's own numeric(4,3), copied and never computed —
                  and set APART from the sentence below, because spec §2 is
                  emphatic that a percentage beside a sentence invites the
                  reader to trust the number instead of reading the sentence. */}
              {formatConfidence(line.proposal.confidence) && (
                <span className="figure" style={{ marginLeft: 8, color: 'var(--ink-faint)', letterSpacing: 0 }}>
                  {formatConfidence(line.proposal.confidence)}
                </span>
              )}
            </>
          )}
        </h2>

        {/* The entry the matcher put forward, named before the reason for it —
            "which entry" and "why that entry" are two questions and the first
            one has a short answer. */}
        {proposed && (
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 2fr', marginBottom: 8 }}>
            <Fact label="Entry">{proposed.entryNo}</Fact>
            <Fact label="Dated">{proposed.transactionDate}</Fact>
            <Fact label="Days apart">{proposed.dayGap}</Fact>
          </div>
        )}

        {/* VERBATIM. The server wrote this to be read by the person deciding;
            summarising it would summarise the evidence. */}
        {line.basis && <div className="note" style={{ marginTop: 0 }}><strong>Why:</strong> {line.basis}</div>}
        <div className="note" style={{ marginTop: 8 }}>{meta.note}</div>

        {line.blockedBy && (
          <div className="note note--warn">
            The entry that fits is claimed by line {line.blockedBy.rowOrdinal} of this
            statement, dated <span className="figure">{line.blockedBy.transactionDate}</span>.
          </div>
        )}

        {line.candidates.length === 0 ? (
          <div className="note">No entry in the ledger has this amount within seven days of it.</div>
        ) : (
          <div className="scroll-x" style={{ marginTop: 14 }}>
            <table className="ledger">
              <thead>
                <tr>
                  <th style={{ width: 34 }}><span className="visually-hidden">Choose</span></th>
                  <th style={{ width: 76 }}>Entry</th>
                  <th style={{ width: 104 }}>Date</th>
                  <th>What the ledger says</th>
                  <th style={{ width: 78 }} className="num">Days</th>
                </tr>
              </thead>
              <tbody>
                {line.candidates.map((c) => {
                  const unavailable = candidateUnavailable(c);
                  return (
                    <tr key={c.journalLineId}>
                      <td>
                        <input type="radio" name={`candidate-${line.statementLineId}`}
                               checked={chosen === c.journalLineId}
                               disabled={unavailable || !canApprove}
                               onChange={() => onPick(line, c.journalLineId)}
                               aria-label={`Entry ${c.entryNo}, ${c.description}`} />
                      </td>
                      <td className="figure">{c.entryNo}</td>
                      <td className="figure">{c.transactionDate}</td>
                      <td>
                        {c.description}
                        {/* The candidate's own sentence, from the server, per
                            candidate. This is what makes choosing by hand a
                            decision rather than a guess. */}
                        {c.basis && <div className="recon__why">{c.basis}</div>}
                        {unavailable && (
                          <div className="recon__why">
                            already matched to the statement line of {c.claimedBy.transactionDate}
                          </div>
                        )}
                      </td>
                      <td className="num figure">{c.dayGap}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!canApprove ? (
          <div className="note note--warn">
            You can read a statement in and run the matcher. Approving a match posts to the general
            ledger — a slip's suspense leg approved here writes Dr slip suspense / Cr bank — so it
            is a colleague holding <em>reconcile.approve</em> who decides.
          </div>
        ) : (
          <>
            {chosen && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
                {/* ⚠ KEYED, NOT A BOOLEAN. See this file's header, and
                    lib/ackKey.js, whose pattern this is. */}
                <input type="checkbox" checked={acknowledged}
                       onChange={() => onToggleAck(line, chosen)} />
                <span>
                  I have checked line <span className="figure">{line.sourceRowNo}</span> of the
                  statement against entry{' '}
                  <span className="figure">{line.candidates.find((c) => c.journalLineId === chosen)?.entryNo ?? '—'}</span>
                  {' '}— approve this match.
                </span>
              </label>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn" disabled={busy || !chosen || !acknowledged}
                      onClick={() => onApprove(line)}>
                {busy ? 'Approving…' : 'Approve this line'}
              </button>
              {/* Reject is never bulk and never ticked: it posts nothing, and
                  it is a permanent tombstone for one pairing (spec §3) that
                  the matcher may never undo. One line, one deliberate click,
                  and the runner-up is proposed on the next run. */}
              {line.proposal && (
                <button className="icon-btn" disabled={busy} onClick={() => onReject(line)}>
                  Not this entry
                </button>
              )}
              {!chosen && (
                <span className="recon__why">
                  Nothing is chosen for this line. Pick an entry above, or leave it.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function BankReconciliation({ entity }) {
  const caps = entity?.capabilities ?? [];
  // `reconcile.approve` is the authorisation half, split from import exactly as
  // `cashbook.post` is split from `cashbook.capture`: the owner's requirement is
  // literally that a second person checks. A client_admin reaches this screen,
  // imports and runs the matcher, and sees no tick and no buttons — the API
  // refuses either way, and this is about not offering a button that answers 403.
  const canApprove = caps.includes('reconcile.approve');

  const [accounts, setAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState('');

  // The file, read and parsed IN THE BROWSER (decision 4). `parsed` is
  // absaStatement.js's answer, verbatim.
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [sha, setSha] = useState(null);

  const [preview, setPreview] = useState(null);
  const [statement, setStatement] = useState(null);
  const [report, setReport] = useState(null);

  const [mode, setMode] = useState('todo');
  const [selectedId, setSelectedId] = useState(null);
  // Which entry a person has pointed at, per statement line. A pick is not an
  // approval and never has been: it changes the ack key, so any tick given for
  // the previous pick lapses on its own.
  const [picked, setPicked] = useState({});
  // ⚠ A SET OF KEYS, NEVER A BOOLEAN AND NEVER A LIST OF ROWS. Nothing resets
  // this and nothing needs to: a key that no longer describes what is on screen
  // simply stops matching. See this file's header.
  const [acked, setAcked] = useState(() => new Set());

  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [refusals, setRefusals] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/accounts`)
      .then((d) => {
        setAccounts(d.accounts);
        const banks = bankAccounts(d.accounts);
        if (banks.length > 0) setBankAccountId((cur) => cur || banks[0].id);
      })
      .catch((e) => setErr(e.message));
  }, [entity?.id]);

  const banks = useMemo(() => bankAccounts(accounts), [accounts]);
  const bank = banks.find((a) => a.id === bankAccountId) ?? null;

  /**
   * Read the file.
   *
   * THE HASH IS OF THE FILE'S BYTES, not of the text or of the parsed rows.
   * `UNIQUE (entity_id, file_sha256)` is what makes re-importing the same
   * export return the same statement with every decision intact (spec §3), and
   * that only holds if the same file always hashes the same way — a hash of
   * re-encoded text would change with a line ending or a byte-order mark.
   */
  const readFile = async (chosen) => {
    setErr(null); setMsg(null); setRefusals([]);
    setPreview(null); setStatement(null); setReport(null);
    setPicked({}); setAcked(new Set()); setSelectedId(null);
    setFile(chosen ?? null);
    setParsed(null); setSha(null);
    if (!chosen) return;

    try {
      const bytes = new Uint8Array(await chosen.arrayBuffer());
      if (!globalThis.crypto?.subtle) {
        // Said plainly rather than posting no hash and letting the server
        // refuse with a sentence about a digest the user never saw.
        setErr('This browser will not hash the file, which is how a statement imported twice is '
               + 'recognised as the same one. Open the app over https and try again.');
        return;
      }
      const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
      setSha([...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join(''));
      setParsed(parseAbsaStatement(new TextDecoder().decode(bytes)));
    } catch (e) { setErr(e.message); }
  };

  const rowsForWire = () => (parsed?.rows ?? []);

  const runPreview = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const d = await api.post(`/api/entities/${entity.id}/bank-statements/preview`, {
        bankAccountId, fileSha256: sha, rows: rowsForWire(),
      });
      setPreview(d.preview);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const runMatcher = useCallback(async (statementId) => {
    const d = await api.post(`/api/bank-statements/${statementId}/matches`);
    setReport(d);
    return d;
  }, []);

  const doImport = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      // ⚠ TWO LEVELS OF `statement`, AND THEY MEAN DIFFERENT THINGS. The route
      // answers `{ statement: <the import RESULT> }`, and the result carries
      // its own `statement` — the header row that was written. The id lives on
      // the inner one; reading it off the outer gives `undefined` and a matcher
      // run against `/bank-statements/undefined/matches`.
      const { statement: result } = await api.post(`/api/entities/${entity.id}/bank-statements`, {
        bankAccountId, fileSha256: sha, rows: rowsForWire(),
      });
      setStatement(result.statement);
      setMsg(result.alreadyImported
        ? 'This file has been imported before. Nothing was written; every decision already made on '
          + 'it is intact.'
        : `Imported ${result.written.lineCount} lines. `
          + `${result.written.cashBookLinesLookedAt} cash book lines are now marked as looked at.`);
      await runMatcher(result.statement.id);
    } catch (e) {
      setErr(e.message);
      // 422 IMPORT_REJECTED lists every broken rule, each naming the row the
      // user sees in their own file. Decision 7: every refusal, never the first.
      setRefusals(e.payload?.errors ?? []);
    } finally { setBusy(false); }
  };

  const rerun = async () => {
    if (!statement) return;
    setBusy(true); setErr(null);
    try {
      await runMatcher(statement.id);
      // A fresh run is fresh evidence. Any tick whose pairing or score changed
      // has already lapsed by key; clearing the rest says so out loud rather
      // than leaving a reviewer to wonder which of their ticks survived.
      setAcked(new Set()); setPicked({});
      setMsg('The matcher ran again. Approved and rejected lines were left alone.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const togglePick = (line, journalLineId) => {
    setPicked((cur) => ({ ...cur, [line.statementLineId]: journalLineId }));
  };

  const toggleAck = (line, journalLineId) => {
    const key = ackKeyForChoice(line, journalLineId);
    if (!key) return;
    setAcked((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  /**
   * Send the decisions.
   *
   * SEQUENTIAL, NOT CONCURRENT, and the reason is the one-claim-per-side rule:
   * two approvals racing for the same journal line is exactly the `23505` the
   * partial unique index exists to raise, and the caller could not say which of
   * their ticks caused it. One at a time, each reported by the row number the
   * user can see.
   */
  const send = async (choices, verb) => {
    if (choices.length === 0) return;
    setBusy(true); setErr(null); setMsg(null); setRefusals([]);
    const failures = [];
    let done = 0;
    for (const choice of choices) {
      try {
        await api.post(
          `/api/bank-statements/${statement.id}/lines/${choice.statementLineId}/${verb}`,
          choice.journalLineId ? { journalLineId: choice.journalLineId } : {},
        );
        done += 1;
      } catch (e) {
        const line = report.lines.find((l) => l.statementLineId === choice.statementLineId);
        failures.push({ row: line?.sourceRowNo ?? null, message: e.message });
      }
    }
    try { await runMatcher(statement.id); } catch (e) { setErr(e.message); }
    setAcked(new Set()); setPicked({}); setSelectedId(null);
    setRefusals(failures);
    setMsg(done > 0
      ? `${done} ${done === 1 ? 'line' : 'lines'} ${verb === 'approve' ? 'approved' : 'rejected'}.`
      : null);
    setBusy(false);
  };

  const lines = report?.lines ?? [];
  const queue = useMemo(() => filterQueue(lines, mode), [lines, mode]);
  const selected = lines.find((l) => l.statementLineId === selectedId) ?? null;
  // WHAT THE BULK BUTTON WOULD SEND, derived from the acknowledgement keys and
  // never from the rows. A row whose evidence changed after it was ticked is
  // not in here, and there is no code path that could put it back.
  const ticked = useMemo(
    () => approvableChoices(lines, acked, picked), [lines, acked, picked],
  );

  const counts = report?.counts ?? null;
  const summary = parsed?.summary ?? null;

  return (
    <>
      <ReportHead entity={entity} title="Bank reconciliation"
                  sub="Read the bank's own statement in, see what the ledger already has against each line, and approve the matches." />

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {refusals.length > 0 && (
        <div className="error">
          <strong>{refusals.length === 1 ? 'One line was refused' : `${refusals.length} lines were refused`}.</strong>
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            {/* THE LIBRARY'S OWN SENTENCE, VERBATIM. Every refusal already
                begins "row 212: " — the number the user sees in their own
                file, which is the whole of decision 7 — so a row number
                printed here as well would print it twice, and the two could
                disagree. */}
            {refusals.map((r, i) => (
              <li key={`${r.row}-${i}`} style={{ marginBottom: 4 }}>{r.message}</li>
            ))}
          </ul>
        </div>
      )}

      {banks.length === 0 && accounts.length > 0 ? (
        <Empty>
          <p>No account on this company is marked as a bank account.</p>
          <p>
            A statement is imported against an account declared a bank — mark them on the chart of
            accounts.
          </p>
        </Empty>
      ) : (
        <>
          <div className="controls">
            <label className="field"><span>Cash book</span>
              <select value={bankAccountId} disabled={Boolean(statement)}
                      onChange={(e) => setBankAccountId(e.target.value)}>
                {banks.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select></label>
            <label className="field"><span>ABSA Cheque Account export (.csv)</span>
              <input type="file" accept=".csv,text/csv"
                     onChange={(e) => readFile(e.target.files?.[0] ?? null)} /></label>
            <button className="btn btn--quiet" disabled={busy || !parsed?.ok || !sha || Boolean(statement)}
                    onClick={runPreview}>
              Check it
            </button>
            <button className="btn" disabled={busy || !preview?.ok || Boolean(statement)}
                    onClick={doImport}>
              {busy ? 'Working…' : 'Import'}
            </button>
            {statement && (
              <button className="btn btn--quiet" disabled={busy} onClick={rerun}>
                Run the matcher again
              </button>
            )}
          </div>

          {/* ---- what the file itself says --------------------------------- */}
          {parsed && !parsed.ok && (
            <div className="error">
              <strong>{file?.name ?? 'That file'} was not read.</strong>
              <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                {parsed.errors.map((e, i) => (
                  <li key={`${e.code}-${i}`} style={{ marginBottom: 4 }}>{e.message}</li>
                ))}
              </ul>
            </div>
          )}

          {summary && parsed.ok && (
            <div className="stat-row">
              <div className="stat">
                <div className="stat__label">Period</div>
                <div className="stat__value" style={{ fontSize: 15 }}>
                  {summary.fromDate} → {summary.toDate}
                </div>
              </div>
              <div className="stat">
                <div className="stat__label">Lines</div>
                <div className="stat__value">{summary.rowCount}</div>
                {/* Zero-amount rows are skipped entirely (owner, 2026-08-29) and
                    the chain is verified over what remains — so this number is
                    said out loud rather than left as a difference nobody can
                    explain. */}
                <div className="stat__sub">{summary.skippedCount} zero-amount rows skipped</div>
              </div>
              <div className="stat">
                <div className="stat__label">Opening balance</div>
                <div className="stat__value"><Money value={centsToAmount(summary.openingBalanceCents)} /></div>
              </div>
              <div className="stat">
                <div className="stat__label">Closing balance</div>
                <div className="stat__value"><Money value={centsToAmount(summary.closingBalanceCents)} /></div>
              </div>
            </div>
          )}

          {parsed?.ok && !statement && (
            <div className="note">
              The running balance carries on unbroken across all {summary.rowCount} lines, which is
              the file's own proof that nothing is missing from it. The server checks it again
              before writing anything.
            </div>
          )}

          {/* ---- what the server made of it -------------------------------- */}
          {preview && !statement && (
            <>
              {preview.warnings.map((w, i) => (
                <div className="note note--warn" key={`${w.code}-${i}`}>{w.message}</div>
              ))}
              {preview.alreadyImported && (
                <div className="note">
                  You have imported this file before. Importing it again writes nothing and returns
                  the statement you already have.
                </div>
              )}
              {preview.ok ? (
                <div className="ok">
                  Nothing in this file stops it being imported.
                  {preview.wouldWrite && (
                    <> {preview.wouldWrite.lineCount} statement lines, and{' '}
                      {preview.wouldWrite.cashBookLinesLookedAt} cash book lines that this period
                      covers would be marked as looked at.</>
                  )}
                </div>
              ) : (
                <div className="error">
                  <strong>This file would be refused.</strong>
                  <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                    {preview.errors.map((e, i) => (
                      <li key={`${e.code}-${i}`} style={{ marginBottom: 4 }}>{e.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* ---- the queue -------------------------------------------------- */}
          {statement && counts && (
            <>
              <div className="stat-row">
                <div className="stat">
                  <div className="stat__label">Statement</div>
                  <div className="stat__value" style={{ fontSize: 15 }}>
                    {bank ? `${bank.code} ${bank.name}` : ''}
                  </div>
                  <div className="stat__sub">{statement.fromDate} → {statement.toDate}</div>
                </div>
                <div className="stat">
                  <div className="stat__label">Proposed</div>
                  <div className="stat__value">{counts.proposed}</div>
                  <div className="stat__sub">{counts.confident} confident · {counts.possible} possible</div>
                </div>
                <div className="stat">
                  <div className="stat__label">Left to you</div>
                  {/* `unmatched` ALREADY EXCLUDES the proposed and the decided
                      — bankMatch.js counts it as "outcome is neither" — so
                      subtracting `proposed` here would subtract them twice and
                      report nought left to do on a statement with four lines
                      nobody has looked at. */}
                  <div className="stat__value">{counts.unmatched}</div>
                  <div className="stat__sub">{counts.tied} tied · {counts.blocked} already matched</div>
                </div>
                <div className="stat">
                  <div className="stat__label">Approved</div>
                  <div className="stat__value">{counts.alreadyDecided}</div>
                  <div className="stat__sub">of {counts.lines} lines</div>
                </div>
              </div>

              <div className="controls">
                <label className="field"><span>Show</span>
                  <select value={mode} onChange={(e) => setMode(e.target.value)}>
                    <option value="todo">Needs a decision</option>
                    <option value="all">Every line</option>
                  </select></label>
                {canApprove && (
                  <button className="btn" disabled={busy || ticked.length === 0}
                          onClick={() => send(ticked, 'approve')}>
                    {ticked.length === 0
                      ? 'Nothing ticked'
                      : `Approve ${ticked.length} ticked ${ticked.length === 1 ? 'line' : 'lines'}`}
                  </button>
                )}
                <span className="recon__why" style={{ alignSelf: 'center' }}>
                  Nothing here approves by itself. A confident match fills the choice in; the tick
                  is still yours.
                </span>
              </div>

              {queue.length === 0 ? (
                <Empty><p>Nothing on this statement is waiting for a decision.</p></Empty>
              ) : (
                <div className="scroll-x">
                  <table className="ledger">
                    <thead>
                      <tr>
                        {canApprove && <th style={{ width: 34 }}>✓</th>}
                        <th style={{ width: 54 }} className="num">Row</th>
                        <th style={{ width: 100 }}>Date</th>
                        <th>Description</th>
                        <th className="num col-credit" style={{ width: 108 }}>Out</th>
                        <th className="num col-debit" style={{ width: 108 }}>In</th>
                        <th style={{ width: 180 }}>What the matcher found</th>
                        <th>Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queue.map((l) => {
                        const meta = outcomeMeta(l.outcome);
                        const chosen = chosenJournalLineId(l, picked);
                        const key = ackKeyForChoice(l, chosen);
                        const band = l.proposal ? bandPresentation(l.proposal.band) : null;
                        const { moneyIn, moneyOut } = amountColumns(l.amountCents);
                        return (
                          <tr key={l.statementLineId}
                              className={`row--drill recon--${meta.tone}`}
                              aria-current={l.statementLineId === selectedId ? 'true' : undefined}
                              style={{ background: l.statementLineId === selectedId ? 'var(--paper-raised)' : undefined }}
                              onClick={() => setSelectedId(l.statementLineId)}>
                            {canApprove && (
                              <td onClick={(e) => e.stopPropagation()}>
                                {/* ⚠ CHECKED AGAINST THE KEY, not against a
                                    per-row boolean. Two identical rows have two
                                    different keys, so this box cannot be the
                                    other row's box. */}
                                <input type="checkbox" disabled={!key}
                                       checked={Boolean(key) && acked.has(key)}
                                       onChange={() => toggleAck(l, chosen)}
                                       aria-label={`Approve line ${l.sourceRowNo}`} />
                              </td>
                            )}
                            <td className="num figure">{l.sourceRowNo}</td>
                            <td className="figure">{l.transactionDate}</td>
                            <td>
                              <button className="cell-link" type="button"
                                      onClick={(e) => { e.stopPropagation(); setSelectedId(l.statementLineId); }}>
                                {l.description}
                              </button>
                            </td>
                            {/* TWO COLUMNS AND NO SIGNS, the way a paper cash
                                book has always been ruled: the column IS the
                                direction, so a minus sign in it would say the
                                same thing twice and a bracket would say it
                                backwards. The sign is the SERVER'S, read and
                                never recomputed — see amountColumns. */}
                            <td className="num col-credit figure">{moneyOut ? money(moneyOut) : ''}</td>
                            <td className="num col-debit figure">{moneyIn ? money(moneyIn) : ''}</td>
                            <td>
                              {meta.label}
                              {band && (
                                <div className="recon__why">
                                  {band.label}{' '}
                                  <span className="figure">{formatConfidence(l.proposal.confidence)}</span>
                                </div>
                              )}
                            </td>
                            {/* THE SERVER'S SENTENCE, VERBATIM, in the queue as
                                well as in the panel — the whole screen only
                                works if a person can see WHY without opening
                                anything. */}
                            <td className="recon__why">{l.basis ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {selected && (
                <LineDetail
                  line={selected} picked={picked} acked={acked}
                  canApprove={canApprove} busy={busy}
                  onPick={togglePick} onToggleAck={toggleAck}
                  onApprove={(l) => send(approvableChoices([l], acked, picked), 'approve')}
                  onReject={(l) => send([{ statementLineId: l.statementLineId, journalLineId: null }], 'reject')}
                />
              )}
            </>
          )}

          {!parsed && !statement && (
            <Empty>
              <p>Choose the ABSA Cheque Account CSV export for this cash book.</p>
              <p>
                It is read here in your browser, checked against its own running balance, and only
                then sent — and checked again on the way in.
              </p>
            </Empty>
          )}
        </>
      )}
    </>
  );
}
